/**
 * Tabellone degli esiti (SPEC-023 §6, F-126).
 *
 * ⚠️ **`pct` è `null` sotto la soglia di numerosità, e arriva così dal server.**
 * Non è un dettaglio di presentazione: un dato che viaggia prima o poi viene
 * mostrato, quindi la percentuale che non si può dire **non esiste** nel DTO.
 * La pagina scrive il conteggio al suo posto.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type OutcomeSpecies =
  | 'instrument_analysis'
  | 'daily_briefing_item'
  | 'daily_pick_result'
  | 'auto_trade_proposal'
  | 'value_trigger';

export type Accuracy = {
  /** `hit` + `miss`. I `neutral` sono esclusi: non sono scommesse. */
  scored: number;
  hits: number;
  /** `null` sotto soglia. Al suo posto si scrive «N casi — troppo pochi». */
  pct: number | null;
  /** Intervallo di Wilson al 95%, `null` insieme a `pct`. */
  lowPct: number | null;
  highPct: number | null;
};

export type ScoreRow = {
  key: string;
  hits: number;
  misses: number;
  neutral: number;
  unmeasurable: number;
  accuracy: Accuracy;
  /** ⚠️ Mediana dell'errore FIRMATO: due errori opposti non si annullano. */
  medianTargetErrorPct: string | null;
  targetErrorCount: number;
};

export type SpeciesCost = {
  subjectType: OutcomeSpecies;
  cost: string | null;
  calls: number;
  estimated: number;
  unknown: number;
  /** `null` sotto soglia: è il numero più facile da leggere male. */
  costPerHit: string | null;
  note: string | null;
};

export type Scoreboard = {
  from: string;
  to: string;
  days: number;
  horizon: number;
  subjectType: OutcomeSpecies | null;
  minSample: number;
  horizons: number[];
  species: OutcomeSpecies[];
  totals: {
    measured: number;
    unmeasurable: number;
    hits: number;
    misses: number;
    neutral: number;
    accuracy: Accuracy;
  };
  bySpecies: ScoreRow[];
  byModel: ScoreRow[];
  byHorizon: ScoreRow[];
  /** ⚠️ Ordinare righe sotto soglia significa inventare una gara e poi crederci. */
  rankable: { species: boolean; model: boolean };
  cost: { currency: 'USD'; bySpecies: SpeciesCost[]; tradingAgentsAnalyses: number };
};

export type InstrumentOutcome = {
  subjectType: string;
  subjectId: string;
  judgedAt: string;
  judgement: string;
  horizonDays: number;
  status: 'hit' | 'miss' | 'neutral' | 'unmeasurable';
  unmeasurableReason: string | null;
  returnPct: string | null;
  /** Rendimento mediano dei titoli giudicati lo stesso giorno: il riferimento onesto. */
  peerReturnPct: string | null;
  targetErrorPct: string | null;
  llmModel: string | null;
};

export const outcomeKeys = {
  all: ['outcomes'] as const,
  board: (days: number, horizon: number, species: string) => ['outcomes', days, horizon, species] as const,
  instrument: (instrumentId: string) => ['outcomes', 'instrument', instrumentId] as const,
};

export function useScoreboard(days: number, horizon: number, species: OutcomeSpecies | 'all') {
  return useQuery({
    queryKey: outcomeKeys.board(days, horizon, species),
    queryFn: ({ signal }) =>
      api.get<Scoreboard>(
        `/outcomes?days=${days}&horizon=${horizon}${species === 'all' ? '' : `&subjectType=${species}`}`,
        signal,
      ),
  });
}

export function useInstrumentOutcomes(instrumentId: string | undefined) {
  return useQuery({
    queryKey: outcomeKeys.instrument(instrumentId ?? ''),
    queryFn: ({ signal }) =>
      api.get<{ instrumentId: string; outcomes: InstrumentOutcome[] }>(
        `/outcomes/instrument/${instrumentId}`,
        signal,
      ),
    enabled: Boolean(instrumentId),
  });
}

/**
 * Gli esiti dei giudizi di un digest, per riga.
 *
 * ⚠️ Una chiamata sola per l'intera pagina, non una per riga: un digest ha
 * decine di titoli, e trenta richieste in parallelo al montaggio sono un
 * autogol che si nota solo in produzione.
 */
export function useBriefingOutcomes(briefingId: string | undefined) {
  return useQuery({
    queryKey: [...outcomeKeys.all, 'briefing', briefingId ?? ''] as const,
    queryFn: ({ signal }) =>
      api.get<{ briefingId: string; byItemId: Record<string, InstrumentOutcome[]> }>(
        `/outcomes/briefing/${briefingId}`,
        signal,
      ),
    enabled: Boolean(briefingId),
  });
}

/**
 * Ricalcolo su richiesta: **aritmetica su dati già pagati**, nessuna chiamata
 * LLM e nessuna rete. Non costa nulla e non perde nulla.
 */
export function useRecomputeOutcomes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ measured: number; unmeasurable: number; failed: number }>('/outcomes/recompute', {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: outcomeKeys.all }),
  });
}

// ---------------------------------------- Profilo dei flussi (F-137)

export type FlowStage = {
  key: string;
  label: string;
  entered: number;
  passed: number;
  /** Le cause di morte, dalla più frequente. Somma = entrati − usciti. */
  deaths: Array<{ reason: string; count: number }>;
  /** ⚠️ `null` = nessun denominatore misurato, non «zero morti». */
  mortalityPct: number | null;
};

export type WasteRow = {
  key: string;
  label: string;
  count: number;
  cost: string | null;
  note: string | null;
};

export type FlowProfile = {
  from: string;
  to: string;
  days: number;
  stages: FlowStage[];
  /** ⚠️ Se l'imbuto non quadra si mostra il difetto, non lo si nasconde. */
  problems: string[];
  waste: { currency: 'USD'; rows: WasteRow[] };
  analyses: { requested: number; completed: number; failed: number; medianMinutes: number | null };
  recalc: {
    minOperationAmount: Array<{ threshold: string; wouldDie: number; wouldSurvive: number; unknown: number }>;
    cappedByMaxProposals: number;
  };
  briefingItems: number;
  measurableOutcomes: number;
};

export function useFlowProfile(days: number) {
  return useQuery({
    queryKey: [...outcomeKeys.all, 'flow', days] as const,
    queryFn: ({ signal }) => api.get<FlowProfile>(`/outcomes/flow?days=${days}`, signal),
  });
}

/**
 * Scarica un export.
 *
 * ⚠️ Passa da `api.getText` e non da un `<a href>`: il collegamento diretto non
 * porterebbe l'intestazione di autenticazione, e il file arriverebbe come un
 * 401 salvato su disco — un guasto che sembra un file corrotto. Il refresh del
 * token su 401 funziona come per ogni altra chiamata.
 */
export async function downloadExport(path: string, fallbackName: string): Promise<void> {
  const body = await api.getText(path);
  const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Rilasciare l'oggetto: senza, ogni export tiene in memoria il proprio file
  // finché la scheda resta aperta.
  URL.revokeObjectURL(url);
}
